import React, { useState, useEffect } from 'react';
import { AdminLayout } from '../../../components/layout/AdminLayout';
import { Link, useNavigate } from 'react-router-dom';
import { Button } from '../../../components/ui/button';
import { Input } from '../../../components/ui/input';
import { 
  Table, TableBody, TableCell, TableHead, 
  TableHeader, TableRow 
} from '../../../components/ui/table';
import { 
  Dialog, DialogContent, DialogHeader, DialogTitle,
  DialogFooter, DialogDescription, DialogClose
} from '../../../components/ui/dialog';
import { 
  Card, CardContent, CardDescription, CardHeader, CardTitle 
} from '../../../components/ui/card';
import { 
  Eye, 
  Edit, 
  Trash2, 
  Plus, 
  Search, 
  Building,
  Clock,
  Link as LinkIcon
} from 'lucide-react';
import { format } from 'date-fns';
import { fr } from 'date-fns/locale';
import { useToast } from '../../../components/ui/use-toast';
import { useCheckPermissions } from '../../../middleware/checkPermissions';
import { 
  fetchPartners,
  deletePartner,
  Partner
} from '../../../services/partnerService';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { supabase } from '../../../lib/supabase';
import { useDomainQuery, getDomainFilterParams } from '../../../hooks/useDomainQuery';
import { applyDomainFilterToQuery } from '../../../hooks/useDomainQuery';

export const PartnerList = () => {
  useCheckPermissions(['admin']);
  const { toast } = useToast();
  const queryClient = useQueryClient();
  
  // Utiliser la logique de domaine
  const { applyDomainFilter, getFilterParams } = useDomainQuery();
  
  const [searchQuery, setSearchQuery] = useState('');
  const [sortBy, setSortBy] = useState<'created_at' | 'name'>('name');
  const [sortOrder, setSortOrder] = useState<'asc' | 'desc'>('asc');
  const [editingName, setEditingName] = useState<{id: string | null, value: string}>({ id: null, value: '' });
  const [editingDescription, setEditingDescription] = useState<{id: string | null, value: string}>({ id: null, value: '' });
  const [editingDate, setEditingDate] = useState<{id: string | null, value: string}>({ id: null, value: '' });
  const [editingLogo, setEditingLogo] = useState<{id: string | null, value: string}>({ id: null, value: '' });
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [partnerToDelete, setPartnerToDelete] = useState<Partner | null>(null);
  const [deletingPartner, setDeletingPartner] = useState(false);

  const updatePartner = async (id: string, updates: Partial<Partner>) => {
    try {
      const { error } = await supabase
        .from('partners')
        .update({
          ...updates,
          updated_at: new Date().toISOString()
        })
        .eq('id', id);

      if (error) throw error;

      toast({
        title: 'Succès',
        description: 'Le partenaire a été mis à jour',
      });

      // Rafraîchir les données
      refetch();
      return true;
    } catch (error) {
      console.error('Erreur lors de la mise à jour du partenaire:', error);
      toast({
        title: 'Erreur',
        description: 'Impossible de mettre à jour le partenaire',
        variant: 'destructive',
      });
      return false;
    }
  };

  const updatePartnerLogo = async (id: string, file: File) => {
    try {
      // Ici vous devrez implémenter la logique de téléchargement du logo
      // et mettre à jour l'URL du logo dans la base de données
      toast({
        title: 'Fonctionnalité à venir',
        description: 'La modification du logo sera bientôt disponible',
      });
      return false;
    } catch (error) {
      console.error('Erreur lors de la mise à jour du logo:', error);
      toast({
        title: 'Erreur',
        description: 'Impossible de mettre à jour le logo',
        variant: 'destructive',
      });
      return false;
    }
  };

  // Utiliser directement le service partnerService qui gère déjà le filtrage domaine
  const { data: allPartners, error, isLoading, refetch } = useQuery({
    queryKey: ['partners'],
    queryFn: fetchPartners
  });

  // Gérer les erreurs avec useEffect
  React.useEffect(() => {
    if (error) {
      console.error('Erreur lors de la récupération des partenaires:', error);
      toast({
        title: 'Erreur',
        description: 'Impossible de récupérer les partenaires',
        variant: 'destructive',
      });
    }
  }, [error, toast]);

  // Filtrer et trier les partenaires côté client
  const partners = React.useMemo(() => {
    if (!allPartners) return [];
    
    let filtered = [...allPartners];
    
    // Appliquer la recherche
    if (searchQuery) {
      filtered = filtered.filter(partner => 
        partner.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
        (partner.description && partner.description.toLowerCase().includes(searchQuery.toLowerCase()))
      );
    }
    
    // Appliquer le tri
    filtered.sort((a, b) => {
      let aValue = sortBy === 'name' ? a.name : a.created_at;
      let bValue = sortBy === 'name' ? b.name : b.created_at;
      
      if (sortBy === 'created_at') {
        aValue = new Date(aValue).getTime();
        bValue = new Date(bValue).getTime();
      }
      
      if (sortOrder === 'asc') {
        return aValue < bValue ? -1 : aValue > bValue ? 1 : 0;
      } else {
        return aValue > bValue ? -1 : aValue < bValue ? 1 : 0;
      }
    });
    
    return filtered;
  }, [allPartners, searchQuery, sortBy, sortOrder]);

  const handleDeleteClick = (partner: Partner) => {
    setPartnerToDelete(partner);
    setDeleteDialogOpen(true);
  };

  const handleDeleteConfirm = async () => {
    if (!partnerToDelete) return;
    
    setDeletingPartner(true);
    
    try {
      await deletePartner(partnerToDelete.id);
      
      toast({
        title: 'Succès',
        description: 'Le partenaire a été supprimé avec succès',
        variant: 'default',
      });
      
      // Rafraîchir les données
      queryClient.invalidateQueries({ queryKey: ['adminPartners'] });
      queryClient.invalidateQueries({ queryKey: ['partners'] });
      
      setDeleteDialogOpen(false);
      setPartnerToDelete(null);
    } catch (error) {
      console.error('Erreur lors de la suppression du partenaire:', error);
      toast({
        title: 'Erreur',
        description: 'Impossible de supprimer le partenaire',
        variant: 'destructive',
      });
    } finally {
      setDeletingPartner(false);
    }
  };

  return (
    <AdminLayout>
      <div className="container mx-auto py-8">
        <div className="flex justify-between items-center mb-8">
          <h1 className="text-3xl font-bold text-white">Gestion des partenaires</h1>
          <Link to="/admin/partners/new">
            <Button className="bg-yellow-500 hover:bg-yellow-600 text-black">
              <Plus className="mr-2 h-4 w-4" /> Nouveau partenaire
            </Button>
          </Link>
        </div>

        {/* Section des statistiques supprimée comme demandé */}

        {/* Barre de recherche */}
        <div className="mb-8">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" size={18} />
            <Input
              type="text"
              placeholder="Rechercher un partenaire..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="pl-10 bg-white/5 border-white/10 text-white placeholder-gray-400 focus:border-yellow-500/50 focus:ring-yellow-500/50"
            />
          </div>
        </div>

        {/* Liste des partenaires */}
        <div className="bg-white/5 backdrop-blur-sm border border-white/10 rounded-2xl overflow-hidden">
          <div className="overflow-x-auto">
            {isLoading ? (
              <div className="flex justify-center py-12">
                <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-yellow-500"></div>
              </div>
            ) : error ? (
              <div className="text-center py-12">
                <p className="text-red-400">Une erreur est survenue lors du chargement des partenaires.</p>
                <Button 
                  onClick={() => refetch()} 
                  variant="outline" 
                  className="mt-4 border-white/10 text-white hover:bg-white/5"
                >
                  Réessayer
                </Button>
              </div>
            ) : partners && partners.length === 0 ? (
              <div className="text-center py-12">
                <Building className="h-12 w-12 text-gray-400 mx-auto mb-4" />
                <p className="text-gray-400 mb-2">Aucun partenaire trouvé</p>
                <Link to="/admin/partners/new">
                  <Button className="bg-yellow-500 hover:bg-yellow-600 text-black mt-2">
                    <Plus className="mr-2 h-4 w-4" /> Ajouter un partenaire
                  </Button>
                </Link>
              </div>
            ) : (
              <table className="w-full">
                <thead>
                  <tr className="border-b border-white/10">
                    <th 
                      className="px-4 py-3 text-left text-sm font-medium text-gray-400 cursor-pointer hover:text-white transition-colors"
                      onClick={() => {
                        if (sortBy === 'name') {
                          setSortOrder(sortOrder === 'asc' ? 'desc' : 'asc');
                        } else {
                          setSortBy('name');
                          setSortOrder('asc');
                        }
                      }}
                    >
                      <div className="flex items-center">
                        Nom
                        {sortBy === 'name' && (
                          <span className="ml-1">
                            {sortOrder === 'asc' ? '↑' : '↓'}
                          </span>
                        )}
                      </div>
                    </th>
                    <th className="px-4 py-3 text-left text-sm font-medium text-gray-400">Description</th>
                    <th className="px-4 py-3 text-left text-sm font-medium text-gray-400">Logo</th>
                    <th 
                      className="px-4 py-3 text-left text-sm font-medium text-gray-400 cursor-pointer hover:text-white transition-colors"
                      onClick={() => {
                        if (sortBy === 'created_at') {
                          setSortOrder(sortOrder === 'asc' ? 'desc' : 'asc');
                        } else {
                          setSortBy('created_at');
                          setSortOrder('desc');
                        }
                      }}
                    >
                      <div className="flex items-center">
                        Date d'ajout
                        {sortBy === 'created_at' && (
                          <span className="ml-1">
                            {sortOrder === 'asc' ? '↑' : '↓'}
                          </span>
                        )}
                      </div>
                    </th>
                    <th className="px-4 py-3 text-right text-sm font-medium text-gray-400">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {partners?.map((partner, index) => (
                    <tr key={partner.id} className={`border-b border-white/5 hover:bg-white/10 ${index % 2 === 0 ? 'bg-white/5' : 'bg-white/10'}`}>
                      <td className="px-4 py-4">
                        {editingName.id === partner.id ? (
                          <div className="relative">
                            <input
                              type="text"
                              value={editingName.value}
                              onChange={(e) => setEditingName({ id: partner.id, value: e.target.value })}
                              className="w-full bg-gray-800 border border-gray-700 rounded px-3 py-2 text-white text-sm"
                              autoFocus
                              onKeyDown={(e) => {
                                if (e.key === 'Enter') {
                                  updatePartner(partner.id, { name: editingName.value });
                                  setEditingName({ id: null, value: '' });
                                } else if (e.key === 'Escape') {
                                  setEditingName({ id: null, value: '' });
                                }
                              }}
                            />
                            <div className="absolute right-0 top-0 mt-1 flex space-x-1">
                              <button
                                onClick={() => setEditingName({ id: null, value: '' })}
                                className="p-1 text-gray-400 hover:text-white"
                                title="Annuler"
                              >
                                ✕
                              </button>
                              <button
                                onClick={async () => {
                                  const success = await updatePartner(partner.id, { name: editingName.value });
                                  if (success) setEditingName({ id: null, value: '' });
                                }}
                                className="p-1 text-green-400 hover:text-green-300"
                                title="Enregistrer"
                              >
                                ✓
                              </button>
                            </div>
                          </div>
                        ) : (
                          <div 
                            className="text-white font-medium cursor-pointer hover:bg-white/5 px-2 py-1 rounded"
                            onClick={() => setEditingName({ id: partner.id, value: partner.name })}
                          >
                            {partner.name}
                          </div>
                        )}
                      </td>
                      <td className="px-4 py-4">
                        {editingDescription.id === partner.id ? (
                          <div className="relative">
                            <textarea
                              value={editingDescription.value}
                              onChange={(e) => setEditingDescription({ id: partner.id, value: e.target.value })}
                              className="w-full bg-gray-800 border border-gray-700 rounded px-3 py-2 text-white text-sm"
                              rows={3}
                              autoFocus
                              onKeyDown={(e) => {
                                if (e.key === 'Enter' && e.ctrlKey) {
                                  updatePartner(partner.id, { description: editingDescription.value });
                                  setEditingDescription({ id: null, value: '' });
                                } else if (e.key === 'Escape') {
                                  setEditingDescription({ id: null, value: '' });
                                }
                              }}
                            />
                            <div className="absolute right-0 top-0 mt-1 flex space-x-1">
                              <button
                                onClick={() => setEditingDescription({ id: null, value: '' })}
                                className="p-1 text-gray-400 hover:text-white"
                                title="Annuler"
                              >
                                ✕
                              </button>
                              <button
                                onClick={async () => {
                                  const success = await updatePartner(partner.id, { description: editingDescription.value });
                                  if (success) setEditingDescription({ id: null, value: '' });
                                }}
                                className="p-1 text-green-400 hover:text-green-300"
                                title="Enregistrer"
                              >
                                ✓
                              </button>
                            </div>
                          </div>
                        ) : (
                          <div 
                            className="text-gray-300 cursor-pointer hover:bg-white/5 px-2 py-1 rounded max-w-xs truncate"
                            onClick={() => setEditingDescription({ id: partner.id, value: partner.description || '' })}
                            title={partner.description || 'Cliquez pour ajouter une description'}
                          >
                            {partner.description || '-'}
                          </div>
                        )}
                      </td>
                      <td className="px-4 py-4">
                        {editingLogo.id === partner.id ? (
                          <div className="relative">
                            <input
                              type="url"
                              value={editingLogo.value}
                              onChange={(e) => setEditingLogo({ id: partner.id, value: e.target.value })}
                              placeholder="https://exemple.com/logo.png"
                              className="w-full bg-gray-800 border border-gray-700 rounded px-3 py-2 text-white text-sm"
                              autoFocus
                              onKeyDown={(e) => {
                                if (e.key === 'Enter') {
                                  updatePartner(partner.id, { logo_url: editingLogo.value });
                                  setEditingLogo({ id: null, value: '' });
                                } else if (e.key === 'Escape') {
                                  setEditingLogo({ id: null, value: '' });
                                }
                              }}
                            />
                            <div className="absolute right-0 top-0 mt-1 flex space-x-1">
                              <button
                                onClick={() => setEditingLogo({ id: null, value: '' })}
                                className="p-1 text-gray-400 hover:text-white"
                                title="Annuler"
                              >
                                ✕
                              </button>
                              <button
                                onClick={async () => {
                                  const success = await updatePartner(partner.id, { logo_url: editingLogo.value });
                                  if (success) setEditingLogo({ id: null, value: '' });
                                }}
                                className="p-1 text-green-400 hover:text-green-300"
                                title="Enregistrer"
                              >
                                ✓
                              </button>
                            </div>
                          </div>
                        ) : (
                          <div 
                            className="relative group cursor-pointer"
                            onClick={() => setEditingLogo({ id: partner.id, value: partner.logo_url || '' })}
                          >
                            {partner.logo_url ? (
                              <div className="w-10 h-10 rounded bg-white/10 flex items-center justify-center overflow-hidden">
                                <img 
                                  src={partner.logo_url} 
                                  alt={partner.name} 
                                  className="w-full h-full object-contain"
                                  onError={(e) => {
                                    (e.target as HTMLImageElement).src = 'data:image/svg+xml;charset=UTF-8,%3Csvg%20width%3D%2224%22%20height%3D%2224%22%20viewBox%3D%220%200%2024%2024%22%20fill%3D%22none%22%20xmlns%3D%22http%3A%2F%2Fwww.w3.org%2F2000%2Fsvg%22%3E%3Cpath%20d%3D%22M4%204H20C21.1%204%2022%204.9%2022%206V18C22%2019.1%2021.1%2020%2020%2020H4C2.9%2020%202%2019.1%202%2018V6C2%204.9%202.9%204%204%204ZM20%206L12%2011L4%206H20ZM4%2018H20V8L12%2013L4%208V18Z%22%20fill%3D%22%239CA3AF%22%2F%3E%3C%2Fsvg%3E';
                                  }}
                                />
                              </div>
                            ) : (
                              <div className="w-10 h-10 rounded bg-white/10 flex items-center justify-center">
                                <span className="text-gray-400">+</span>
                              </div>
                            )}
                            <div className="absolute inset-0 bg-black/50 opacity-0 group-hover:opacity-100 flex items-center justify-center transition-opacity">
                              <span className="text-xs text-white">Modifier</span>
                            </div>
                          </div>
                        )}
                      </td>
                      <td className="px-4 py-4">
                        {editingDate.id === partner.id ? (
                          <div className="relative">
                            <input
                              type="datetime-local"
                              value={editingDate.value}
                              onChange={(e) => setEditingDate({ id: partner.id, value: e.target.value })}
                              className="w-full bg-gray-800 border border-gray-700 rounded px-3 py-2 text-white text-sm"
                              autoFocus
                              onKeyDown={(e) => {
                                if (e.key === 'Enter') {
                                  updatePartner(partner.id, { created_at: editingDate.value });
                                  setEditingDate({ id: null, value: '' });
                                } else if (e.key === 'Escape') {
                                  setEditingDate({ id: null, value: '' });
                                }
                              }}
                            />
                            <div className="absolute right-0 top-0 mt-1 flex space-x-1">
                              <button
                                onClick={() => setEditingDate({ id: null, value: '' })}
                                className="p-1 text-gray-400 hover:text-white"
                                title="Annuler"
                              >
                                ✕
                              </button>
                              <button
                                onClick={async () => {
                                  const success = await updatePartner(partner.id, { created_at: editingDate.value });
                                  if (success) setEditingDate({ id: null, value: '' });
                                }}
                                className="p-1 text-green-400 hover:text-green-300"
                                title="Enregistrer"
                              >
                                ✓
                              </button>
                            </div>
                          </div>
                        ) : (
                          <div 
                            className="text-gray-300 cursor-pointer hover:bg-white/5 px-2 py-1 rounded"
                            onClick={() => {
                              const date = new Date(partner.created_at);
                              const localDateTime = new Date(date.getTime() - (date.getTimezoneOffset() * 60000))
                                .toISOString()
                                .slice(0, 16);
                              setEditingDate({ id: partner.id, value: localDateTime });
                            }}
                            title="Cliquez pour modifier la date"
                          >
                            {format(new Date(partner.created_at), 'dd MMM yyyy HH:mm', { locale: fr })}
                          </div>
                        )}
                      </td>
                      <td className="px-4 py-4">
                        <div className="flex justify-end space-x-2">
                          <Link to={`/admin/partners/edit/${partner.id}`}>
                            <Button variant="ghost" size="sm" className="text-yellow-400 hover:text-yellow-300">
                              <Edit size={18} />
                            </Button>
                          </Link>
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => handleDeleteClick(partner)}
                            className="text-red-400 hover:text-red-300"
                          >
                            <Trash2 size={18} />
                          </Button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>
        </div>
      </div>

      {/* Dialogue de confirmation de suppression */}
      <Dialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
        <DialogContent className="sm:max-w-md bg-black/95 border border-white/10 text-white">
          <DialogHeader>
            <DialogTitle className="text-xl font-bold text-white">Supprimer le partenaire</DialogTitle>
            <DialogDescription className="text-gray-400">
              Êtes-vous sûr de vouloir supprimer ce partenaire ? Cette action est irréversible.
            </DialogDescription>
          </DialogHeader>

          {partnerToDelete && (
            <div className="mt-4 p-4 bg-white/5 border border-white/10 rounded-xl">
              <div className="flex items-center">
                <Building className="h-5 w-5 text-blue-400 mr-2" />
                <span className="font-medium text-white">{partnerToDelete.name}</span>
              </div>
              {partnerToDelete.description && (
                <p className="mt-2 text-sm text-gray-400 line-clamp-2">{partnerToDelete.description}</p>
              )}
            </div>
          )}

          <DialogFooter className="mt-6">
            <Button
              variant="outline"
              onClick={() => setDeleteDialogOpen(false)}
              className="border-white/10 text-white hover:bg-white/5"
            >
              Annuler
            </Button>
            <Button
              variant="destructive"
              onClick={handleDeleteConfirm}
              disabled={deletingPartner}
              className="bg-red-500 hover:bg-red-600 text-white"
            >
              {deletingPartner ? (
                <>
                  <Clock className="mr-2 h-4 w-4 animate-spin" />
                  Suppression...
                </>
              ) : (
                <>
                  <Trash2 className="mr-2 h-4 w-4" />
                  Supprimer
                </>
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </AdminLayout>
  );
};

export default PartnerList;
